import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from './engineDebug.js';
/**
 * BacigaLogic class handles the core rules and scoring for the Baciga game.
 */
class BacigaLogic {
    constructor() {
        this.POINTS_LIMIT = 31;
    }

    // ─── Point values and win threshold (RONDA_ONLINE.md Fase 12) ─────
    // Centralized here instead of scattered as literals across Baciga.jsx
    // (awardPoints/handleAction31/end31Phase/checkAndValidateBaciga), same
    // motivation as resolveBacigaWinner's own hardcoded 7/2 just below —
    // a single source of truth the backend can reuse without re-deriving
    // magic numbers from UI orchestration code.
    static POINTS = Object.freeze({
        BACIGOTE: 7,
        BACIGA: 2,
        LAS_31: 2,
        EL_PUNTO: 1,
        LAS_COSAS: 18,
        BUST: -1,
        BUST_WITH_VALIDATED_BACIGA: -3,
        RIVAL_BONUS_ON_BUST: 2,
    });

    // A team reaching this many points ends the round (Baciga.jsx checks
    // `marker.score >= 18` at ~10 call sites instead of a shared constant).
    static HAND_WIN_THRESHOLD = 18;

    // ─── Phase state machine ────────────────────────────────────────
    // Canonical phase graph for a Báciga hand, centralized here instead of
    // being implicit in scattered setPhase() calls in the component, so a
    // transition can be validated before it's applied (RONDA_ONLINE.md
    // section 3.1: "impedir que un cliente pueda ejecutar una acción que
    // no corresponda al estado actual"). 'roundEnd' loops back to 'dealing'
    // for the next hand of the same round.
    static PHASES = Object.freeze({
        INIT: 'init',
        DEALING: 'dealing',
        VOTING: 'voting',
        PLAYING_31: 'playing31',
        ROUND_END: 'roundEnd',
    });

    static PHASE_TRANSITIONS = Object.freeze({
        init: ['dealing'],
        dealing: ['voting'],
        voting: ['playing31'],
        playing31: ['roundEnd'],
        roundEnd: ['dealing'],
    });

    /**
     * Whether transitioning from `fromPhase` to `toPhase` is a legal move
     * in the Báciga phase graph.
     * @param {string} fromPhase
     * @param {string} toPhase
     * @returns {boolean}
     */
    isValidPhaseTransition(fromPhase, toPhase) {
        return (BacigaLogic.PHASE_TRANSITIONS[fromPhase] || []).includes(toPhase);
    }

    /**
     * Calculates scores for a set of cards properly handling Comodines (7 Oros, 7 Copas).
     * Comodines can take ANY value from 1-10, so:
     * - For Báciga check: we calculate MINIMUM possible score (comodines = 1)
     * - For Las 31: we calculate OPTIMAL score closest to 31
     * 
     * @param {Array} cards - Array of card objects { suit, value }
     * @returns {Object} { points: number, minPoints: number, isBaciga: boolean, isBacigote: boolean, isCosas: boolean }
     */
    calculateScore(cards) {
        if (!cards || cards.length === 0) return { points: 0, minPoints: 0, isBaciga: true, isBacigote: false, isCosas: false };

        const comodines = cards.filter(card => this.isComodin(card));
        // Calculate base sum of non-wildcards (and wildcards with assigned values effectively become non-wildcards for calculation purposes)
        const activeComodines = cards.filter(card => this.isComodin(card) && !this.isCardBound(card));
        const boundComodines = cards.filter(card => this.isComodin(card) && this.isCardBound(card));
        const regularCards = cards.filter(card => !this.isComodin(card));

        // Base sum includes regular cards and bound comodines
        const baseSum = regularCards.reduce((sum, card) => sum + this.getCardValue(card), 0) +
            boundComodines.reduce((sum, card) => sum + this.getCardValue(card), 0);

        // Handle Bacigote (Three of a kind) check - already handles comodines
        const isBacigote = this.checkBacigote(cards);

        // Handle Las Cosas (Four of a kind) check
        const isCosas = this.checkCosas(cards);

        // Calculate MINIMUM score (for Báciga check) - active (unbound) comodines count as 1
        const minScore = baseSum + (activeComodines.length * 1);

        // Calculate OPTIMAL score for Las 31 - active comodines optimized to get closest to 31
        let optimalScore = baseSum;

        if (activeComodines.length > 0) {
            const remainder = 31 - baseSum;

            // If we have multiple wildcards, we have more flexibility.
            // If we have 1 wildcard, we can add 1-7 or 10. We CANNOT add 8 or 9.

            if (remainder <= 0) {
                // Already at or over 31, add minimum (1 per comodin)
                optimalScore = baseSum + activeComodines.length;
            } else {
                // We want to get as close to 31 as possible without busting.
                // If we can reach exactly 31, do it.
                // If not, get as close as possible without going over.

                // Helper to check if a target sum can be formed by N wildcards
                // Valid values for each WC: 1,2,3,4,5,6,7,10
                const canFormSum = (target, numWildcards) => {
                    if (target < numWildcards * 1) return false; // Too small (min 1 each)
                    if (target > numWildcards * 10) return false; // Too big (max 10 each)

                    if (numWildcards === 1) {
                        // Single wildcard cannot be 8 or 9
                        return target !== 8 && target !== 9;
                    }

                    // For 2+ wildcards, can we form gaps?
                    // 2 WC: Min 2, Max 20.
                    // Can we make 18? 10+8 (no), 10+9 (no), but 10+10=20. 
                    // Wait, 8 and 9 are invalid values.
                    // With 2 cards:
                    // 10+7=17. 10+10=20. 
                    // Gaps for 2 cards: 18 (10+8), 19 (10+9).
                    // Actually 7+7=14. 
                    // We can make anything from 2..14.
                    // We can make 10+1..7 = 11..17.
                    // We can make 10+10 = 20.
                    // So for 2 cards, we CANNOT make: 18, 19.

                    if (numWildcards === 2) {
                        return target !== 18 && target !== 19;
                    }

                    // For 3+ cards, usually no gaps in upper range because we have enough small numbers?
                    // 3 cards Max 30. 
                    // 10+10+8 no... 10+10+10=30.
                    // 10+10+7=27.
                    // Gaps: 28, 29.
                    if (numWildcards === 3) {
                        return target !== 28 && target !== 29;
                    }

                    // General rule: If we use K tens, remaining sum must be formable by N-K cards.
                    // Checking all combinations is safer but maybe overkill?
                    // For N <= 4, simple checks work.

                    // Let's implement a simple recursive check or just assume valid reasonable range.
                    // Since max wildcards is usually low (max 4 in deck), checking exact logic is better.

                    // Recursive solver
                    return checkSumRecursive(target, numWildcards);
                };

                const checkSumRecursive = (tgt, count) => {
                    if (count === 0) return tgt === 0;
                    // Try all valid values for one card
                    const values = [1, 2, 3, 4, 5, 6, 7, 10];
                    for (let v of values) {
                        if (tgt - v >= (count - 1)) { // Optimization: remaining must be at least 1*(count-1)
                            if (checkSumRecursive(tgt - v, count - 1)) return true;
                        }
                    }
                    return false;
                };

                // Check if we can reach EXACTLY remainder
                if (canFormSum(remainder, activeComodines.length)) {
                    optimalScore = 31;
                } else {
                    // Can't make 31 exactly. Find highest possible sum <= remainder
                    let bestAdd = activeComodines.length; // Start with min possible (All 1s)

                    // Search upwards from min to remainder
                    // Actually, we want the defined max possible below remainder.
                    // Brute force check sums from remainder-1 down to min?
                    for (let t = remainder - 1; t >= activeComodines.length; t--) {
                        if (canFormSum(t, activeComodines.length)) {
                            bestAdd = t;
                            break;
                        }
                    }

                    optimalScore = baseSum + bestAdd;
                }
            }
        }

        // Báciga: minimum possible score <= 9
        const isBaciga = minScore <= 9;

        debugLog(`🎯 [BacigaLogic] Cards: ${cards.map(c => `${c.value} ${c.suit}${c.assignedValue ? `(${c.assignedValue})` : ''}`).join(', ')}`);
        debugLog(`🎯 [BacigaLogic] Active Comodines: ${activeComodines.length}, Bound: ${boundComodines.length}`);
        debugLog(`🎯 [BacigaLogic] Comodines detected: ${comodines.map(c => `${c.value} ${c.suit}`).join(', ') || 'none'}`);
        debugLog(`🎯 [BacigaLogic] BaseSum: ${baseSum}, MinScore: ${minScore}, OptimalScore: ${optimalScore}`);
        debugLog(`🎯 [BacigaLogic] isBaciga: ${isBaciga}, isBacigote: ${isBacigote}`);

        return {
            points: optimalScore,      // Used for Las 31 phase
            minPoints: minScore,       // Used for display/info
            isBaciga,                  // True if player can call Báciga
            isBacigote,                // True if player can call Bacigote
            isCosas
        };
    }

    /**
     * Gets the point value of a card.
     * 10, 11, 12 count as 10.
     * Others count as face value.
     * Comodines return 0 (handled separately)
     */
    getCardValue(card) {
        if (this.isComodin(card) && !this.isCardBound(card)) return 0; // Handled separately if unbound
        if (this.isComodin(card) && this.isCardBound(card)) {
            const val = card.assignedValue;
            if (val === 10 || val === 11 || val === 12) return 10;
            return val;
        }
        if (['10', '11', '12'].includes(card.value.toString())) return 10;
        return parseInt(card.value, 10);
    }

    /**
     * Checks if a card is a Comodin (7 Oros or 7 Copas).
     */
    isComodin(card) {
        const value = parseInt(card.value, 10);
        return value === 7 && (card.suit === 'oros' || card.suit === 'copas');
    }

    /**
     * Checks if a card has a fixed assigned value
     */
    isCardBound(card) {
        return this.isComodin(card) && card.assignedValue !== undefined && card.assignedValue !== null;
    }

    /**
     * Determines optimal value for an unbound wildcard given the current hand
     * @param {Object} card - The wildcard card
     * @param {Array} currentHand - All cards in hand including the wildcard
     * @returns {number} Optimal value (1-10)
     */
    /**
     * Determines optimal value for an unbound wildcard given the current hand.
     * Evaluates all possible values (1-7, 10-12) and chooses the one that maximizes:
     * 1. Bacigote (Three of a kind)
     * 2. Báciga (Sum <= 9)
     * 3. Pairs/Trios
     * 
     * @param {Object} card - The wildcard card
     * @param {Array} currentHand - All cards in hand including the wildcard
     * @returns {number} Optimal value (1-12)
     */
    getOptimalWildcardValue(card, currentHand) {
        if (!this.isComodin(card)) return this.getCardValue(card);

        // Possible values to assign: 1-7, 10-12
        const possibleValues = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        let bestValue = 1;
        let maxScore = -Infinity;

        // Mock hand excludes current card to simulate adding it with new value
        const otherCards = currentHand.filter(c => c !== card);

        possibleValues.forEach(val => {
            // Create a mock card with this assigned value
            const mockCard = { ...card, assignedValue: val };
            const mockHand = [...otherCards, mockCard];

            // Evaluate this hand
            let score = 0;

            // 1. Check for Bacigote (Highest Priority)
            if (this.checkBacigote(mockHand)) {
                score += 1000;
            }

            // 2. Check for Báciga
            // Calculate sum for Báciga check
            // Note: For other unbound wildcards in hand, assume optimal (min) value of 1 for Báciga check
            const baseSum = mockHand.reduce((sum, c) => {
                if (this.isComodin(c) && !this.isCardBound(c)) return sum + 1; // Assume 1 for other wildcards
                return sum + this.getCardValue(c);
            }, 0);

            if (baseSum <= 9) {
                score += 500;
                // Tie-breaker: Lower sum is better for winning Báciga
                score -= baseSum; // e.g. Sum 5 (score 495) > Sum 9 (score 491)
            }

            // 3. Check for Pairs/Trios (Ligues)
            // Use helper but need to handle unbound other cards?
            // countPairsAndTrios handles activeComodines by adding to max group
            const pairsInfo = this.countPairsAndTrios(mockHand);
            score += (pairsInfo.pairs * 100);
            score += (pairsInfo.trios * 300); // Trio is basically Bacigote, but good to reinforce

            // 4. Fallback: Las 31 optimization?
            // If no Báciga (sum > 9) and no pairs, try to hit 31?
            // Or just high cards? 
            // For now, prioritize low pair over high non-pair.

            debugLog(`🤖 [BacigaLogic] Testing Value ${val}: Score ${score} (Sum ${baseSum})`);

            if (score > maxScore) {
                maxScore = score;
                bestValue = val;
            }
        });

        debugLog(`🤖 [BacigaLogic] Optimal Wildcard Value: ${bestValue} (Score ${maxScore})`);
        return bestValue;
    }

    /**
     * Get optimal wildcard value for Las 31 phase
     * Prioritizes based on difficulty:
     * 
     * EASY: Only avoid busting (going over 31). Don't optimize for ligues or 31.
     * NORMAL: Try to make ligues if possible without busting, and try to reach 31.
     * HARD: Optimal play - maximize points. Try to reach 31, make ligues, but never bust.
     *
     * @param {Object} card - The wildcard card
     * @param {Array} currentHand - All cards in hand including the wildcard
     * @param {string} difficulty - 'EASY', 'NORMAL', or 'HARD' (default: 'NORMAL')
     * @param {boolean} someoneHas31 - Whether someone else already has exactly 31 in this round
     * @returns {number} Optimal value (1-12)
     */
    getOptimalWildcardValueForLas31(card, currentHand, difficulty = 'NORMAL', someoneHas31 = false) {
        if (!this.isComodin(card)) return this.getCardValue(card);

        // Possible values to assign: 1-7, 10-12
        const possibleValues = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        let bestValue = 1;
        let maxScore = -Infinity;

        // Mock hand excludes current card to simulate adding it with new value
        const otherCards = currentHand.filter(c => c !== card);

        // Count existing card values (excluding unbound wildcards)
        const valueCounts = {};
        otherCards.forEach(c => {
            if (!this.isComodin(c) || this.isCardBound(c)) {
                const val = this.isComodin(c) && this.isCardBound(c) ? c.assignedValue : parseInt(c.value, 10);
                valueCounts[val] = (valueCounts[val] || 0) + 1;
            }
        });

        // Calculate base sum of other cards (for bust check)
        const baseSum = otherCards.reduce((sum, c) => {
            if (this.isComodin(c) && !this.isCardBound(c)) return sum; // Skip unbound wildcards
            return sum + this.getCardValue(c);
        }, 0);

        // Helper to get point value for a card value (10, 11, 12 count as 10)
        const getPointsForValue = (val) => {
            if (val === 10 || val === 11 || val === 12) return 10;
            return val;
        };

        possibleValues.forEach(val => {
            const pointsForThisValue = getPointsForValue(val);
            const totalSum = baseSum + pointsForThisValue;
            
            // CRITICAL: If this value causes bust, heavily penalize (never pick it)
            if (totalSum > 31) {
                debugLog(`🤖 [BacigaLogic] Las31 Testing Value ${val}: BUST (${totalSum} > 31) - REJECTED`);
                return; // Skip this value entirely
            }

            let score = 0;
            const countWithWildcard = (valueCounts[val] || 0) + 1;

            // Scoring logic based on difficulty
            if (difficulty === 'EASY') {
                // EASY: Only care about not busting. Prefer lower values to stay safe.
                // No optimization for ligues or 31.
                score = 100 - pointsForThisValue; // Lower values are safer
                
            } else if (difficulty === 'NORMAL') {
                // NORMAL: Try to make ligues if possible without busting, and try to reach 31
                
                // 1. Check for Las Cosas (4 of a kind) - HIGHEST PRIORITY
                if (countWithWildcard >= 4) {
                    score += 2000;
                }

                // 2. Check if this makes exactly 31
                if (totalSum === 31) {
                    score += 1000; // High priority for 31
                }

                // 3. Check for trios (3 of a kind for ligues) - but only if doesn't bust
                if (countWithWildcard >= 3) {
                    score += 500;
                }

                // 4. Check for pairs (2 of a kind for ligues)
                if (countWithWildcard >= 2) {
                    score += 150;
                }

                // 5. Tiebreaker: prefer higher values to get closer to 31
                score += pointsForThisValue;
                
            } else if (difficulty === 'HARD') {
                // HARD: Optimal play - maximize everything
                // Priority: Las Cosas > 31 with ligues > 31 alone > ligues > high points
                
                // 1. Check for Las Cosas (4 of a kind) - instant win
                if (countWithWildcard >= 4) {
                    score += 5000;
                }

                // 2. Check if this makes exactly 31
                const makes31 = totalSum === 31;
                
                // 3. Check for ligues
                const makesTrio = countWithWildcard >= 3;
                const makesPair = countWithWildcard >= 2;

                if (makes31) {
                    // If no one else has 31 yet, prioritize making 31
                    if (!someoneHas31) {
                        score += 2000;
                    } else {
                        // Someone already has 31, so 31 alone doesn't help
                        // But 31 + ligues still gives us ligues points
                        score += 500; // Small bonus for tying
                    }
                    
                    // Bonus for 31 WITH ligues (best possible outcome)
                    if (makesTrio) {
                        score += 1500; // 31 + trio is amazing
                    } else if (makesPair) {
                        score += 800; // 31 + pair is great
                    }
                } else {
                    // Can't make 31, focus on ligues and high points
                    if (makesTrio) {
                        score += 1000;
                    }
                    if (makesPair) {
                        score += 400;
                    }
                    
                    // Prefer getting closer to 31 (higher points)
                    score += pointsForThisValue * 5;
                }
            }

            debugLog(`🤖 [BacigaLogic] Las31 [${difficulty}] Testing Value ${val}: Score ${score} (sum: ${totalSum}, count: ${countWithWildcard})`);

            if (score > maxScore) {
                maxScore = score;
                bestValue = val;
            }
        });

        debugLog(`🤖 [BacigaLogic] Optimal Wildcard Value for Las31 [${difficulty}]: ${bestValue} (Score ${maxScore})`);
        return bestValue;
    }

    checkBacigote(cards) {
        if (cards.length !== 3) return false;

        // Get effective ranks (handling comodines)
        // If 2 cards + 1 comodin: Bacigote if 2 cards match.
        // If 1 card + 2 comodines: Always Bacigote.
        // If 3 cards: Must match.

        const nonComodines = cards.filter(c => !this.isComodin(c) || this.isCardBound(c));

        // If we have bound comodines, they act as their assigned value
        // BUT for pairs/trios involving figures/numbers, we compare VALUES (1-12), not POINTS (1-10)
        // This is tricky. Comodín assigns a NUMBER (e.g. "King" -> 12).
        // My getCardValue returns POINTS.
        // We need to track ASSIGNED NUMBER (1-12) for Bacigote check.

        const getCardNumber = (c) => {
            if (this.isComodin(c) && this.isCardBound(c)) return c.assignedValue;
            return parseInt(c.value, 10);
        };

        const activeComodines = cards.filter(c => this.isComodin(c) && !this.isCardBound(c)).length;

        // If all are flexible comodines -> Automatic Bacigote
        if (activeComodines === 3) return true;

        // Group by numeric value (1-12)
        const counts = {};
        nonComodines.forEach(c => {
            const val = getCardNumber(c);
            counts[val] = (counts[val] || 0) + 1;
        });

        // Check if any existing value group + active comodines >= 3
        return Object.values(counts).some(count => count + activeComodines >= 3);
    }

    checkCosas(cards) {
        // Need at least 4 cards
        if (!cards || cards.length < 4) return false;

        const getCardNumber = (c) => {
            if (this.isComodin(c) && this.isCardBound(c)) return c.assignedValue;
            return parseInt(c.value, 10);
        };

        const activeComodines = cards.filter(c => this.isComodin(c) && !this.isCardBound(c)).length;
        const nonComodines = cards.filter(c => !this.isComodin(c) || this.isCardBound(c));

        // If we have 4+ active wildcards, that's automatic Las Cosas
        if (activeComodines >= 4) return true;

        const counts = {};
        nonComodines.forEach(c => {
            const val = getCardNumber(c);
            counts[val] = (counts[val] || 0) + 1;
        });

        // Check if any value + active comodines >= 4
        return Object.values(counts).some(count => count + activeComodines >= 4);
    }

    /**
     * Resolves who wins the Baciga voting phase.
     * @param {Array} declarations - Array of { playerIndex, type: 'baciga'|'bacigote', minPoints, cards }
     * @param {number} manoPlayerIndex - The player who goes first (mano)
     * @returns {Object|null} - { winnerIndex, type, points } or null if no valid declarations
     * 
     * Rules:
     * 1. Bacigote beats Baciga always
     * 2. For multiple Bacigotes: Lower card value wins (e.g., 3 twos beats 3 fives)
     * 3. For multiple Bácigas: Lower sum wins
     * 4. Ties: Player closer to "mano" (turn order) wins
     */
    resolveBacigaWinner(declarations, manoPlayerIndex) {
        if (!declarations || declarations.length === 0) return null;

        // Separate bacigotes from bacigas
        const bacigotes = declarations.filter(d => d.type === 'bacigote');
        const bacigas = declarations.filter(d => d.type === 'baciga');

        debugLog('🏆 [BacigaLogic] Resolving winner:', { bacigotes, bacigas, manoPlayerIndex });

        // Helper to determine order relative to mano
        const getOrderFromMano = (playerIdx) => {
            return (playerIdx - manoPlayerIndex + 4) % 4;
        };

        // If there are bacigotes, they take precedence
        if (bacigotes.length > 0) {
            // Sort by card value (lower is better), then by mano order
            bacigotes.sort((a, b) => {
                if (a.minPoints !== b.minPoints) {
                    return a.minPoints - b.minPoints;
                }
                return getOrderFromMano(a.playerIndex) - getOrderFromMano(b.playerIndex);
            });

            const winner = bacigotes[0];
            debugLog('🏆 [BacigaLogic] Bacigote winner:', winner.playerIndex);
            return {
                winnerIndex: winner.playerIndex,
                type: 'bacigote',
                points: BacigaLogic.POINTS.BACIGOTE,
                allDeclarations: declarations
            };
        }

        // No bacigotes, check bacigas
        if (bacigas.length > 0) {
            // Sort by minPoints (lower is better), then by mano order
            bacigas.sort((a, b) => {
                if (a.minPoints !== b.minPoints) {
                    return a.minPoints - b.minPoints;
                }
                return getOrderFromMano(a.playerIndex) - getOrderFromMano(b.playerIndex);
            });

            const winner = bacigas[0];
            debugLog('🏆 [BacigaLogic] Báciga winner:', winner.playerIndex, 'with', winner.minPoints, 'points');
            return {
                winnerIndex: winner.playerIndex,
                type: 'baciga',
                points: BacigaLogic.POINTS.BACIGA,
                allDeclarations: declarations
            };
        }

        return null;
    }

    /**
     * Points lost when a player busts (score > 31) during Las 31 — pure
     * extraction of Baciga.jsx's handleAction31 bust branch (lines
     * ~2870-2883): a player whose báciga had already been VALIDATED loses
     * both the báciga (-2) and the bust penalty (-1), i.e. -3 total; anyone
     * else just loses -1.
     * @param {boolean} hadValidatedBaciga
     * @returns {number} Negative points to subtract.
     */
    getBustPenalty(hadValidatedBaciga) {
        return hadValidatedBaciga
            ? -Math.abs(BacigaLogic.POINTS.BUST_WITH_VALIDATED_BACIGA)
            : -Math.abs(BacigaLogic.POINTS.BUST);
    }

    /**
     * Determine the winner of a finished Las 31 phase among non-busted
     * final scores — pure extraction of Baciga.jsx's end31Phase (lines
     * ~3282-3309): an exact 31 always wins ("Las 31"), first in turn order
     * if more than one; otherwise the highest score wins ("El Punto"), ties
     * broken by turn order.
     * @param {Array<{playerIndex: number, points: number}>} turnOrderedScores - non-busted scores, already in turn order starting from mano
     * @returns {{winnerIndex: number, type: 'las31'|'elPunto', points: number}|null}
     */
    resolveHandEndWinner(turnOrderedScores) {
        if (!turnOrderedScores || turnOrderedScores.length === 0) return null;

        const thirtyOnes = turnOrderedScores.filter(s => s.points === 31);
        if (thirtyOnes.length > 0) {
            return { winnerIndex: thirtyOnes[0].playerIndex, type: 'las31', points: BacigaLogic.POINTS.LAS_31 };
        }

        let best = turnOrderedScores[0];
        for (const s of turnOrderedScores) {
            if (s.points > best.points) best = s;
        }
        return { winnerIndex: best.playerIndex, type: 'elPunto', points: BacigaLogic.POINTS.EL_PUNTO };
    }

    /**
     * Calculate pairs and trios in a hand
     * @param {Array} cards - Player's cards
     * @returns {Object} - { pairs: number, trios: number, points: number }
     */
    countPairsAndTrios(cards) {
        const getCardNumber = (c) => {
            if (this.isComodin(c) && this.isCardBound(c)) return c.assignedValue;
            if (this.isComodin(c)) return null; // Unbound comodin counts as nothing for automatic extraction?? 
            // Logic: at end of game, AI assigns values. 
            // For humans, unassigned comodines should probably not happen at scoring time if app enforces it.
            // If they do happen, they don't count for pairs unless we optimistically matching them.
            // Rules say: "Los puntos se anotan... poner valor a los comodines que aún no lo poseen" (page 34).
            // So they MUST have value. Assuming they do for now.
            return parseInt(c.value, 10);
        };

        const counts = {};
        let activeComodines = 0;

        cards.forEach(c => {
            if (this.isComodin(c) && !this.isCardBound(c)) {
                activeComodines++;
            } else {
                const val = getCardNumber(c);
                counts[val] = (counts[val] || 0) + 1;
            }
        });

        // If there are unbound comodines at scoring time, we should make them match optimally for pairs?
        // Rules say player assigns value.
        // For simplicity here, assuming they are bound. 
        // If activeComodines > 0 here, it means logic was called before assignment.
        // We'll treat them as wildcards that match the highest existing count group.

        if (activeComodines > 0) {
            // Find best group to add to
            const values = Object.keys(counts);
            if (values.length > 0) {
                // Sort by current count descending
                values.sort((a, b) => counts[b] - counts[a]);
                // Add all comodines to the most populated group (to make trios/cosas)
                counts[values[0]] += activeComodines;
            } else {
                // Only comodines? They match each other.
                // Arbitrarily assign them to value '1' (or whatever)
                counts['1'] = activeComodines;
            }
        }

        let pairs = 0;
        let trios = 0;

        Object.values(counts).forEach(count => {
            if (count === 2) pairs++;
            if (count === 3) trios++;
        });

        // 1 point per pair, 3 points per trio
        const points = (pairs * 1) + (trios * 3);

        return { pairs, trios, points };
    }
}

export default BacigaLogic;
